/**
 * useEntityDetail — TanStack Query hooks for the T2 entity-detail screens
 * (Story V10-J2).
 *
 * live mode → the real endpoints via the typed client (see entity-detail-api).
 * mock mode → the existing entity-detail mock selectors (mock layer NOT
 *             deleted; UI renders standalone without a backend).
 *
 * Tabs WITHOUT a real endpoint (Dokumente/Schäden/Aufgaben/Compliance/Historie)
 * use `mockOnlyList` — mock rows in mock mode, EMPTY in live mode (never mock
 * junk against a real subject id).
 *
 * [Source: apps/web/src/features/customers/hooks/useKundeRows.ts] (hook pattern)
 * [Source: docs/stories/v10-j2-web-wiring-rest.md AC5]
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../lib/api-client';
import { qk } from '../../../lib/query-keys';
import { isLiveData } from '../../../lib/data-mode';
import { mapCustomerToKundeRow } from '../../customers/api/customers-api';
import { useCarrierRows } from '../../carriers/hooks/useCarrierRows';
import type { PolicyVersionDto } from '@brokly/shared';
import {
  currentPremiumOf,
  currentVersionOf,
  mapCommToKomm,
  mapInvoiceToKundeRechnung,
  mapPolicyDtoToView,
  mapRelationshipToBeziehung,
} from '../api/entity-detail-api';
import {
  getKunde,
  getPolicy,
  policiesForKunde,
  policiesForCarrier,
  rechnungenForKunde,
  kommForKunde,
  beziehungenForKunde,
} from '../mock';
import { policyVersionsFor } from '../../policies/mock';
import type { Beziehung, Komm, KundeRechnung, KundeRow, Policy } from '../types';

type SubjectType = 'person' | 'firma';

/** Mock-only tab data: mock rows in mock mode, EMPTY in live mode (V10-J2). */
export function mockOnlyList<T>(fn: () => T[]): T[] {
  return isLiveData() ? [] : fn();
}

// ── Kunde header ──────────────────────────────────────────────────────────────

export interface KundeDetail {
  kunde: KundeRow;
  subjectType: SubjectType;
}

function mockKundeDetail(id: string): KundeDetail | null {
  const kunde = getKunde(id);
  // Mock Kunden are firms (T2 mock dataset).
  return kunde ? { kunde, subjectType: 'firma' } : null;
}

/** The Kunde header row (live: GET /customers lookup by subject_id). */
export function useKundeDetail(id: string) {
  const live = isLiveData();
  return useQuery<KundeDetail | null>({
    queryKey: qk.customers({ mode: live ? 'live' : 'mock', detail: id }),
    queryFn: async () => {
      if (!live) return mockKundeDetail(id);
      const response = await apiClient.customers.list();
      const item = response.items.find((i) => i.subject_id === id);
      return item
        ? { kunde: mapCustomerToKundeRow(item), subjectType: item.subject_type }
        : null;
    },
    ...(live ? {} : { initialData: () => mockKundeDetail(id), staleTime: Infinity }),
  });
}

// ── Policen ───────────────────────────────────────────────────────────────────

/** Overlay: swap carrier_uid for the register display name when known. */
function useCarrierNameOverlay(rows: Policy[] | undefined): Policy[] {
  const live = isLiveData();
  const { data: carriers = [] } = useCarrierRows();
  return useMemo(() => {
    if (!rows) return [];
    if (!live) return rows;
    const nameById = new Map(carriers.map((c) => [c.id, c.name]));
    return rows.map((p) =>
      p.versichererId && nameById.has(p.versichererId)
        ? { ...p, versicherer: nameById.get(p.versichererId)! }
        : p,
    );
  }, [rows, carriers, live]);
}

/** The Kunde Policen tab (live: GET /policies?subject_id=). */
export function usePoliciesForKunde(kundeId: string, kundeName = '') {
  const live = isLiveData();
  const query = useQuery<Policy[]>({
    queryKey: qk.policies({ subject: kundeId, mode: live ? 'live' : 'mock' }),
    queryFn: async () => {
      if (!live) return policiesForKunde(kundeId);
      const dtos = await apiClient.policies.list({ subject_id: kundeId });
      return dtos.map((d) => mapPolicyDtoToView(d, { kundeName }));
    },
    ...(live ? {} : { initialData: () => policiesForKunde(kundeId), staleTime: Infinity }),
  });
  const data = useCarrierNameOverlay(query.data);
  return { ...query, data };
}

/** The Versicherer Policen tab (live: GET /policies?carrier_uid=). */
export function usePoliciesForCarrier(carrierId: string, carrierName: string) {
  const live = isLiveData();
  const query = useQuery<Policy[]>({
    queryKey: qk.policies({ carrier: carrierId, mode: live ? 'live' : 'mock' }),
    queryFn: async () => {
      if (!live) return policiesForCarrier(carrierName);
      // Kunde display names via the customers list (same wire the T1 grid uses).
      const [dtos, customers] = await Promise.all([
        apiClient.policies.list({ carrier_uid: carrierId }),
        apiClient.customers.list(),
      ]);
      const nameBySubject = new Map(customers.items.map((c) => [c.subject_id, c.name ?? '']));
      return dtos.map((d) =>
        mapPolicyDtoToView(d, { kundeName: nameBySubject.get(d.subject_id) ?? '' }),
      );
    },
    ...(live ? {} : { initialData: () => policiesForCarrier(carrierName), staleTime: Infinity }),
  });
  const data = useCarrierNameOverlay(query.data);
  return { ...query, data };
}

// ── Police detail ─────────────────────────────────────────────────────────────

export interface PoliceDetail {
  policy: Policy;
  subjectType: SubjectType;
  /**
   * DMS M12: the open policy_version (`valid_to === null`) — feeds the
   * Stammdaten "Finanzen" card (Stempelabgabe/Nettoprämie) and the
   * "Dokumente öffnen" → M11 viewer trigger (`source_document_id`).
   * `null` when the policy has no open version (never invented on the UI side).
   */
  currentVersion: PolicyVersionDto | null;
}

function mockPoliceDetail(id: string): PoliceDetail | null {
  const policy = getPolicy(id);
  return policy
    ? { policy, subjectType: 'firma', currentVersion: currentVersionOf(policyVersionsFor(id)) }
    : null;
}

/**
 * One Police (live: GET /policies/:id + /versions for the current premium +
 * GET /customers for the holder display name).
 */
export function usePoliceDetail(id: string) {
  const live = isLiveData();
  const query = useQuery<PoliceDetail | null>({
    queryKey: qk.policy(id),
    queryFn: async () => {
      if (!live) return mockPoliceDetail(id);
      const [dto, versions, customers] = await Promise.all([
        apiClient.policies.get(id),
        apiClient.policies.versions(id),
        apiClient.customers.list(),
      ]);
      const holder = customers.items.find((c) => c.subject_id === dto.subject_id);
      return {
        policy: mapPolicyDtoToView(dto, {
          kundeName: holder?.name ?? '',
          praemie: currentPremiumOf(versions),
        }),
        subjectType: dto.subject_type === 'person' ? 'person' : 'firma',
        currentVersion: currentVersionOf(versions),
      };
    },
    ...(live ? {} : { initialData: () => mockPoliceDetail(id), staleTime: Infinity }),
  });

  // Carrier display-name overlay on the single policy.
  const overlaid = useCarrierNameOverlay(
    useMemo(() => (query.data ? [query.data.policy] : undefined), [query.data]),
  );
  const data = query.data ? { ...query.data, policy: overlaid[0] ?? query.data.policy } : query.data;
  return { ...query, data };
}

/**
 * usePolicyCurrentDocumentState — DMS Part2 P2-13: resolves a policy's CURRENT
 * `policy_version.source_document_id` so a caller (the Policen-tab Aktionen cell)
 * can hand it to `DeleteDocumentButton`, which re-checks the document's LIVE state
 * itself. Reuses `GET /policies/:id/versions` (no new wire) — the same query key
 * `usePolicyVersions` uses, so the two hooks share one cache entry when both fire.
 *
 * `enabled` lets a non-admin caller skip the fetch entirely (zero cost — the row
 * never needs a delete control it can't use).
 */
export function usePolicyCurrentDocumentState(policyId: string, enabled: boolean) {
  const live = isLiveData();
  const query = useQuery<PolicyVersionDto[]>({
    queryKey: qk.policyVersions(policyId),
    queryFn: () =>
      live ? apiClient.policies.versions(policyId) : Promise.resolve(policyVersionsFor(policyId)),
    enabled,
    ...(live ? {} : { staleTime: Infinity }),
  });
  const sourceDocumentId = currentVersionOf(query.data ?? [])?.source_document_id ?? null;
  return { sourceDocumentId, isLoading: query.isLoading };
}

// ── Rechnungen / Kommunikation / Beziehungen (Kunde tabs) ────────────────────

/** The Kunde Rechnungen tab (live: GET /invoices?subject…). */
export function useRechnungenForKunde(kundeId: string, subjectType?: SubjectType) {
  const live = isLiveData();
  return useQuery<KundeRechnung[]>({
    queryKey: qk.invoices({ subject: kundeId, subjectType, mode: live ? 'live' : 'mock' }),
    queryFn: async () => {
      if (!live) return rechnungenForKunde(kundeId);
      if (!subjectType) return [];
      const response = await apiClient.invoices.list({
        subject_type: subjectType,
        subject_id: kundeId,
      });
      return response.items.map((i) => mapInvoiceToKundeRechnung(i, kundeId));
    },
    enabled: !live || Boolean(subjectType),
    ...(live ? {} : { initialData: () => rechnungenForKunde(kundeId), staleTime: Infinity }),
  });
}

/** The Kunde Kommunikation tab (live: GET /communications, all three buckets). */
export function useKommForKunde(kundeId: string, subjectType?: SubjectType) {
  const live = isLiveData();
  return useQuery<Komm[]>({
    queryKey: qk.communications('kunde', { subject: kundeId, subjectType, mode: live ? 'live' : 'mock' }),
    queryFn: async () => {
      if (!live) return kommForKunde(kundeId);
      if (!subjectType) return [];
      const buckets = ['eingang', 'ausgang_entwuerfe', 'gesendet'] as const;
      const responses = await Promise.all(
        buckets.map((bucket) =>
          apiClient.communications.list({
            bucket,
            subject_type: subjectType,
            subject_id: kundeId,
            limit: 50,
          }),
        ),
      );
      return responses
        .flatMap((response, i) =>
          response.data.map((item) => mapCommToKomm(item, buckets[i], kundeId)),
        )
        .sort((a, b) => (a.datum < b.datum ? 1 : -1));
    },
    enabled: !live || Boolean(subjectType),
    ...(live ? {} : { initialData: () => kommForKunde(kundeId), staleTime: Infinity }),
  });
}

/**
 * The customer status overlay (live: GET /customer-status/:type/:id) — #6.
 *
 * Surfaces the "(in Verarbeitung)" overlay + displayStatus while an Analysemandat is out for
 * signature. Live-only: mock mode has no overlay endpoint, so it stays undefined and the badge
 * simply doesn't render (the plain status badge from the customers list is the fallback).
 */
export function useCustomerStatusOverlay(kundeId: string, subjectType?: SubjectType) {
  const live = isLiveData();
  return useQuery({
    queryKey: qk.customerStatusOverlay(subjectType ?? 'unknown', kundeId),
    queryFn: () => apiClient.customerStatus.get(subjectType!, kundeId),
    enabled: live && Boolean(subjectType) && Boolean(kundeId),
  });
}

/**
 * The per-customer Vorgang lifecycle board (live: GET /vorgaenge/board?subjectType=) — #6.
 *
 * Returns the mandate Vorgänge for this customer with the API-computed lifecycle
 * (versandt / erinnert / überfällig / eingegangen / …). Live-only; EMPTY in mock mode.
 */
export function useVorgaengeForKunde(kundeId: string, subjectType?: SubjectType) {
  const live = isLiveData();
  return useQuery({
    queryKey: qk.vorgangBoard(subjectType ?? 'unknown', kundeId),
    queryFn: async () => {
      const res = await apiClient.vorgaenge.board({
        subjectType: subjectType!,
        subjectId: kundeId,
        limit: 20,
      });
      return res.items;
    },
    enabled: live && Boolean(subjectType) && Boolean(kundeId),
  });
}

/**
 * The per-customer Mandatskacheln (live: GET /customers/:type/:id/mandate-badges) - P4-S1 (7.6).
 *
 * Two API-computed tiles (Analysemandat, Brokermandat): state + German label + ids + date + actor.
 * Live-only; undefined in mock mode (the header simply omits the tiles).
 */
export function useMandateBadges(kundeId: string, subjectType?: SubjectType) {
  const live = isLiveData();
  return useQuery({
    queryKey: qk.mandateBadges(subjectType ?? 'unknown', kundeId),
    queryFn: () => apiClient.mandateWizard.badges(subjectType!, kundeId),
    enabled: live && Boolean(subjectType) && Boolean(kundeId),
  });
}

/**
 * The customer's live Mandatsauflösung (live: GET /customers/:type/:id/termination), or null (P6). The same
 * key as the dialog «Mandat auflösen» and the task peek, so one invalidation refreshes all of them. Live-only.
 */
export function useLiveTermination(kundeId: string, subjectType?: SubjectType) {
  const live = isLiveData();
  return useQuery({
    queryKey: qk.termination(subjectType ?? 'unknown', kundeId),
    queryFn: () => apiClient.mandateTermination.live(subjectType!, kundeId),
    enabled: live && Boolean(subjectType) && Boolean(kundeId),
  });
}

/**
 * The customer's Portalstatus (live: GET /customers/:type/:id/portal-status) (10.5).
 * Live-only; undefined in mock mode (the header omits the chip).
 */
export function usePortalStatus(kundeId: string, subjectType?: SubjectType) {
  const live = isLiveData();
  return useQuery({
    queryKey: qk.portalStatus(subjectType ?? 'unknown', kundeId),
    queryFn: () => apiClient.mandateWizard.portalStatus(subjectType!, kundeId),
    enabled: live && Boolean(subjectType) && Boolean(kundeId),
  });
}

/** The Kunde Beziehungen tab (live: GET /persons|firms/:id/relationships). */
export function useBeziehungenForKunde(kundeId: string, subjectType?: SubjectType) {
  const live = isLiveData();
  return useQuery<Beziehung[]>({
    queryKey: qk.relationships(subjectType ?? 'unknown', kundeId),
    queryFn: async () => {
      if (!live) return beziehungenForKunde(kundeId);
      if (!subjectType) return [];
      const items = await apiClient.relationships.listForSubject(subjectType, kundeId);
      return items.map((item) => mapRelationshipToBeziehung(item, kundeId));
    },
    enabled: !live || Boolean(subjectType),
    ...(live ? {} : { initialData: () => beziehungenForKunde(kundeId), staleTime: Infinity }),
  });
}
